import React from 'react';
import { motion } from 'framer-motion';

interface AffiliateDisclosureGateProps {
  /** Backend-provided disclosure copy from /config, shown as the honest note. */
  disclosureText: string;
  onAccept: () => void;
  onDecline: () => void;
}

const spring = { type: 'spring', stiffness: 420, damping: 36, mass: 0.9 } as const;

const PERKS = [
  'Products matched to your skin profile',
  'Real prices and where to buy',
  'Options across every budget',
  'The research, done for you',
];

/**
 * The affiliate-disclosure consent screen shown inside the product
 * recommendation popup before any product-search call is made. Rendered as the
 * modal panel — the surrounding scrim comes from ProductSearchModal.
 */
export const AffiliateDisclosureGate: React.FC<AffiliateDisclosureGateProps> = ({
  disclosureText,
  onAccept,
  onDecline,
}) => {
  return (
    <motion.div
      key="affiliate-gate-panel"
      initial={{ y: 48, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      exit={{ y: 32, opacity: 0 }}
      transition={spring}
      style={{
        position: 'relative',
        width: '100%',
        maxHeight: '92vh',
        display: 'flex',
        flexDirection: 'column',
        background: 'var(--c-cream)',
        borderRadius: 'var(--r-xl) var(--r-xl) 0 0',
        boxShadow: 'var(--sh-deep)',
        overflow: 'hidden',
      }}
      className="sm:max-w-md sm:rounded-[var(--r-xl)]"
      onClick={(e) => { e.stopPropagation(); }}
    >
      {/* Top accent line */}
      <div
        style={{
          position: 'absolute', insetInline: 0, top: 0, height: 2, zIndex: 2,
          background: 'linear-gradient(to right, transparent, var(--c-kivo-teal), transparent)',
          pointerEvents: 'none',
        }}
      />
      {/* Brand ambient orbs */}
      <div style={{ position: 'absolute', top: 0, right: 0, width: 220, height: 220, borderRadius: '50%', filter: 'blur(64px)', transform: 'translate(30%,-45%)', background: 'var(--c-teal-weak)', pointerEvents: 'none' }} />
      <div style={{ position: 'absolute', bottom: 0, left: 0, width: 180, height: 180, borderRadius: '50%', filter: 'blur(64px)', transform: 'translate(-25%,45%)', background: 'rgba(244,168,142,0.10)', pointerEvents: 'none' }} />

      <div style={{ position: 'relative', zIndex: 1, padding: 'var(--s-6) var(--s-5) var(--s-5)', overflowY: 'auto' }}>
        {/* Icon */}
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 'var(--s-4)' }}>
          <div
            style={{
              width: 60, height: 60, borderRadius: '50%',
              background: 'var(--c-teal-weak)',
              border: '1px solid rgba(42,164,166,0.25)',
              color: 'var(--c-kivo-teal)',
              display: 'grid', placeItems: 'center',
            }}
          >
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round">
              <path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4Z" />
              <path d="M3 6h18" />
              <path d="M16 10a4 4 0 0 1-8 0" />
            </svg>
          </div>
        </div>

        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: 'var(--s-4)' }}>
          <span className="t-label" style={{ color: 'var(--c-kivo-teal)' }}>Product matches</span>
          <h3 className="t-h3" style={{ margin: '4px 0 6px' }}>Ready to see your matches?</h3>
          <p className="t-small t-mute" style={{ margin: 0, lineHeight: 1.6 }}>
            We line up products that actually fit your skin — so you skip the endless scrolling.
          </p>
        </div>

        {/* Perks */}
        <div
          style={{
            padding: 'var(--s-4)',
            background: 'var(--c-teal-weak)',
            borderRadius: 'var(--r-md)',
            border: '1px solid rgba(42,164,166,0.2)',
            marginBottom: 'var(--s-4)',
          }}
        >
          <div className="stack-2">
            {PERKS.map((perk) => (
              <div key={perk} style={{ display: 'flex', alignItems: 'center', gap: 'var(--s-2)' }}>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" style={{ color: 'var(--c-kivo-teal)', flexShrink: 0 }}>
                  <circle cx="12" cy="12" r="10" stroke="currentColor" />
                  <path d="M9 12l2 2 4-4" stroke="currentColor" />
                </svg>
                <span className="t-small" style={{ color: 'var(--ink)' }}>{perk}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Soft, honest disclosure (backend copy, with a friendly default) */}
        <p className="t-small t-mute" style={{ margin: '0 0 4px', lineHeight: 1.6, textAlign: 'center' }}>
          {disclosureText ||
            'A few links may earn us a small commission — at no extra cost to you, and it never changes what we show you.'}
        </p>
        <div style={{ textAlign: 'center', marginBottom: 'var(--s-5)' }}>
          <a
            href="/affiliate-disclosure"
            target="_blank"
            rel="noopener noreferrer"
            className="t-label"
            style={{ color: 'var(--c-kivo-teal)', textDecoration: 'underline', textUnderlineOffset: 2 }}
          >
            Read the full Affiliate Disclosure
          </a>
        </div>

        {/* Actions */}
        <button
          type="button"
          onClick={onAccept}
          className="btn btn--primary"
          style={{ width: '100%', justifyContent: 'center' }}
        >
          Show my matches
        </button>
        <button
          type="button"
          onClick={onDecline}
          className="btn btn--ghost btn--sm"
          style={{ width: '100%', justifyContent: 'center', marginTop: 'var(--s-2)' }}
        >
          Not now
        </button>
      </div>
    </motion.div>
  );
};
